'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import { useRouter, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import {
  ResetPasswordFormFields,
  ResetPasswordFormSchemaType,
  resetPasswordFormSchema,
} from './ResetPasswordForm.schema'
import { apiClient, ClientRoutes } from '@/config'
import { appSelectors, MessageSeverity, useAppStore } from '@/store'
import { Button, FormField, Input, LinkButton, Text } from '@/components'
import { useLocalizedRoutes } from '@/lib/hooks'

export const ResetPasswordForm: React.FC = () => {
  const form = useForm<ResetPasswordFormSchemaType>({
    mode: 'onTouched',
    resolver: yupResolver(resetPasswordFormSchema),
  })

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = form

  const router = useRouter()
  const searchParams = useSearchParams()
  const { setLoading, setMessage } = useAppStore(appSelectors.actions)
  const { loading } = useAppStore(appSelectors.status)
  const { isLoggedIn } = useAppStore(appSelectors.auth)

  const t = useTranslations('auth.resetPasswordForm')
  const { get: _ } = useLocalizedRoutes()

  const token = (searchParams.get(ResetPasswordFormFields.token) || '')
    .trim()
    .replace(/ /g, '+')

  useEffect(() => {
    if (!token || isLoggedIn) {
      router.push(_(ClientRoutes.forgotPassword))
    }
  }, [token, router, isLoggedIn, _])

  useEffect(() => {
    setValue(ResetPasswordFormFields.token, token)
  }, [token, setValue])

  useEffect(() => {
    if (errors?.token) {
      setMessage({
        message: t('error.incorrectVerificationCode'),
        severity: MessageSeverity.ERROR,
      })
    }
  }, [errors, setMessage, t])

  const onSubmitValid: SubmitHandler<ResetPasswordFormSchemaType> =
    useCallback(async () => {
      const formValues = getValues()
      setLoading(true)
      try {
        const { data } = await apiClient.postResetPassword(formValues)
        if (!data?.success) {
          setMessage({
            message: t('error.incorrectVerificationCode'),
            severity: MessageSeverity.ERROR,
          })
          setLoading(false)
        } else {
          setMessage({
            message: t('success'),
            severity: MessageSeverity.SUCCESS,
          })

          reset({
            [ResetPasswordFormFields.password]: '',
            [ResetPasswordFormFields.passwordConfirmation]: '',
            [ResetPasswordFormFields.token]: '',
          })
        }
      } catch (error) {
        console.error('Error resetting password:', error)
        setMessage({
          message: t('error.incorrectVerificationCode'),
          severity: MessageSeverity.ERROR,
        })
      }
      setLoading(false)
    }, [getValues, reset, setLoading, setMessage, t])

  const onClickSubmit = useCallback(() => {
    handleSubmit(onSubmitValid, () => {
      setMessage({
        message: t('error.invalidForm'),
        severity: MessageSeverity.ERROR,
      })
    })()
  }, [handleSubmit, onSubmitValid, setMessage, t])

  useEffect(() => {
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <FormProvider {...form}>
      <div className="w-full flex flex-col gap-2">
        <Text variant="heading2Xl" className="mt-12 mb-3">
          {t('title')}
        </Text>
        <Text variant="heading" className="mb-6">
          {t('subheader')}
        </Text>

        <div className="text-center">
          <div className="my-3">
            <FormField
              control={control}
              name={ResetPasswordFormFields.password}
              disabled={loading}
              render={({ field }) => (
                <Input
                  type="password"
                  {...field}
                  placeholder={t('fields.password')}
                  className="px-6 py-4 h-12 w-full text-xl rounded-full text-base placeholder:text-base bg-primary"
                />
              )}
            />
          </div>
          <div className="my-3">
            <FormField
              control={control}
              name={ResetPasswordFormFields.passwordConfirmation}
              disabled={loading}
              render={({ field }) => (
                <Input
                  type="password"
                  {...field}
                  placeholder={t('fields.passwordConfirmation')}
                  className="px-6 py-4 h-12 w-full text-xl rounded-full text-base placeholder:text-base bg-primary"
                />
              )}
            />
          </div>
        </div>
        <div className="flex flex-col">
          <Button
            disabled={loading}
            onClick={() => onClickSubmit()}
            variant="glow"
            className="w-full my-3 rounded-full px-8 py-6 text-primary text-lg"
          >
            {t('buttonCaption')}
          </Button>
        </div>
      </div>
      <div className="text-center text-gray-100 mt-8">
        <Text variant="heading">{t('dontHaveAccount')}</Text>
        <LinkButton
          className="my-6 mx-auto p-6 px-8 rounded-full text-lg"
          variant="outline"
          size="lg"
          href={_(ClientRoutes.signUp)}
        >
          {t('signUp')}
        </LinkButton>
      </div>
    </FormProvider>
  )
}
